import * as React from "react";

import { Alert, Snackbar } from "@mui/material";

import { ExtendedAccount } from "@/types/ExtendedAccount";
import {
  Account,
  AccountNextStep,
  AccountType,
  Person,
  ServiceProvider,
  ServiceProviderType,
  YesNo,
} from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { TextInput } from "@/components/atoms/TextInput";
import { AccountNextStepInput } from "@/components/molecules";
import { FormField } from "@/components/molecules/FormField";
import { useOnChange, useOnValueChange, useUpdater } from "@/utils/callbacks";

import { SelectInput } from "@/components/atoms/SelectInput";
import { BooleanInput } from "@/components/atoms/BooleanInput";
import { FormStack } from "@/components/molecules/FormStack";
import { AccountFieldProps } from "./types/AccountFieldProps";
import { NestedErrors } from "@/utils/Types";
import {
  Responsible,
  errorsOfResponsible,
  responsibleFromAccount,
  ResponsibleForm,
} from "./ResponsibleFields/ResponsibleFields";

export type MortgageAccount = {
  readonly holderNumber?: string;
  readonly accountType?: AccountType;
  readonly accountTypeOther?: string;
  readonly jointClaim?: string;
  readonly accountRequest?: AccountNextStep;
  readonly responsible?: Responsible;
};

export type MortgageAccountErrors = NestedErrors<MortgageAccount> | undefined;

export const errorsOf: (r: MortgageAccount) => MortgageAccountErrors = (record) => {
  return cleanedErrors({
    accountRequest: record?.accountRequest ? undefined : "required",
    responsible: errorsOfResponsible(record?.responsible, record?.accountRequest),
  });
};

export const recordFromAccount = (account?: Account): MortgageAccount => {
  if (!account) {
    return {
      responsible: responsibleFromAccount(account),
    };
  }

  return {
    accountType: account.accountType,
    accountTypeOther: account.accountTypeOther,
    holderNumber: account.holderNumber,
    accountRequest: account.nextSteps,
    responsible: responsibleFromAccount(account),
    jointClaim: account.jointClaim,
  };
};

export type MortgageAccountFieldsProps = AccountFieldProps;

export const MortgageAccountFields: React.FC<MortgageAccountFieldsProps> = ({
  persons,
  serviceProvider,
  account,
  setBusy,
  onAccountAdded,
  updateTemplate,
  saveAccount,
  saveRecord,
  savedRecord,
}) => {
  const [record, update] = React.useState(savedRecord || recordFromAccount(account));
  const [errors, setErrors] = React.useState({} as MortgageAccountErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);

  const onAccountRequestValueChange = React.useCallback(
    (accountRequest) => {
      update((s: any) => ({
        ...s,
        accountRequest,
        responsible: {},
      }));
    },
    [update]
  );

  const updateAccountHolder = useUpdater(update, "responsible");
  const onChange = useOnChange(update);
  const onAccountTypeValueChange = useOnValueChange(update, "accountType");

  const onJointClaimValueChanged = React.useCallback(
    (jointClaim) => {
      update((s: any) => ({
        ...s,
        jointClaim: jointClaim ? YesNo.Yes : YesNo.No,
      }));
    },
    [update]
  );

  React.useEffect(() => {
    if (!updateTemplate) {
      return;
    }

    updateTemplate({
      onNext: !serviceProvider?.serviceProviderType
        ? undefined
        : () => {
            const errors = errorsOf(record);

            if (errors) {
              setErrors(errors);
              if (saveRecord) {
                saveRecord({ source: record });
              }
              return;
            }

            const responsible = {
              id: record.responsible?.responsibleId || undefined,
              details: record.responsible?.responsible || undefined,
              bankAccount: record.responsible?.responsibleBankAccount || undefined,
            };

            const data = {
              id: account?.id,
              serviceProvider: {
                id: serviceProvider.serviceProviderId,
                companyName: serviceProvider.customProviderName,
                serviceProviderType: serviceProvider.serviceProviderType,
              },

              holderNumber: record.holderNumber,
              accountType: record.accountType,
              accountTypeOther: record.accountTypeOther,
              jointClaim: record.jointClaim,
              nextStep: record.accountRequest,
              responsible,
            };

            if (saveRecord) {
              saveRecord({ source: record, target: data });
              return { response: {} };
            }

            setBusy(true);

            return saveAccount({
              sector: ServiceProviderType.Mortgage,
              record: data,
            }).then(
              (
                response: {
                  data: {
                    account: ExtendedAccount;
                    newServiceProvider?: ServiceProvider;
                    newPerson?: Person;
                  };
                } & { error: Error }
              ) => {
                setBusy(false);
                if (response.error) {
                  setRemoteErrors(response.error.message);
                  return;
                }
                if (response.data) {
                  onAccountAdded(response.data.account, {
                    newServiceProvider: response.data.newServiceProvider,
                    newPerson: response.data.newPerson,
                  });
                  return;
                }
              },
              (err: Error) => {
                console.warn({ err });
                setBusy(false);
                setRemoteErrors("Operation failed. Please try again or contact customer support.");
              }
            );
          },
    });
  }, [updateTemplate, serviceProvider, account, onAccountAdded, record, setBusy, saveAccount]);

  return (
    <>
      <FormField halfWidth label="Customer reference number (if known)">
        {" "}
        <TextInput
          name="holderNumber"
          value={record.holderNumber || ""}
          onChange={onChange}
          error={errors?.holderNumber}
        />{" "}
      </FormField>

      <FormField halfWidth label="Mortgage type (if known)">
        {" "}
        <SelectInput
          name="accountType"
          value={record.accountType || ""}
          onValueChange={onAccountTypeValueChange}
          error={errors?.accountType}
          options={accountTypeOptions}
        />
        {record.accountType === "other" && (
          <FormStack substack spacing={1}>
            {" "}
            <FormField label="Please provide the mortgage type">
              {" "}
              <TextInput
                name={"accountTypeOther"}
                value={record.accountTypeOther || ""}
                error={errors?.accountTypeOther}
                onChange={onChange}
              />{" "}
            </FormField>{" "}
          </FormStack>
        )}
      </FormField>

      <FormField>
        {" "}
        <BooleanInput
          label="This is a joint policy"
          value={record.jointClaim === YesNo.Yes}
          onValueChange={onJointClaimValueChanged}
        />{" "}
      </FormField>

      <FormField label="Next steps">
        {" "}
        <AccountNextStepInput
          onValueChange={onAccountRequestValueChange}
          value={record.accountRequest || ""}
          serviceProviderType={ServiceProviderType.Mortgage}
          error={errors?.accountRequest}
        />{" "}
      </FormField>

      {record.responsible && record.accountRequest && (
        <ResponsibleForm
          serviceProvider={serviceProvider}
          persons={persons}
          nextStep={record.accountRequest}
          record={record.responsible}
          errors={errors?.responsible}
          update={updateAccountHolder}
        />
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};

const accountTypeOptions = [
  { value: AccountType.Remortgage, label: "Remortgage" },
  { value: AccountType.FirstTimeBuyer, label: "First-time buyer" },
  { value: AccountType.BuyToLet, label: "Buy-to-let" },
  { value: AccountType.Other, label: "Other" },
];
